import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Mail, Building2, ChevronDown, ChevronRight,
  Star, BarChart3, MessageSquare, Briefcase, Filter, FileText,
} from "lucide-react";
import { listLeads } from "@/lib/api";

const SOURCE_META = {
  giggle:    { label: "Giggle",    color: "#DE0A87", Icon: Star },
  portfolio: { label: "Portfolio", color: "#F5B400", Icon: Briefcase },
  quote:     { label: "Quotes",    color: "#C98F00", Icon: FileText },
  contact:   { label: "Contact",   color: "#0F1E2E", Icon: MessageSquare },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AdminLeads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await listLeads(200);
      setLeads(r?.leads || []);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter((l) => filter === "all" || l.source === filter);
  const counts = leads.reduce((acc, l) => { acc[l.source] = (acc[l.source] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-cream-50 text-ink-900 grain" data-testid="admin-leads-page">
      <header className="flex items-center justify-between px-8 md:px-14 py-7">
        <div className="flex items-center gap-6">
          <button onClick={() => nav("/admin")} data-testid="leads-back"
                  className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Admin
          </button>
          <div className="hidden md:flex items-center gap-2 text-ink-900/65">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <span className="serif text-sm">CompliSun</span>
            <span className="mono text-[10px] uppercase tracking-[0.22em] ml-1">Leads</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/admin/edit/portfolio")} data-testid="leads-edit-portfolio"
                  className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors px-3 py-2 border border-ink-900/10 rounded-md">
            Edit Portfolio copy
          </button>
          <button onClick={load} data-testid="leads-refresh"
                  className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors px-3 py-2 border border-ink-900/10 rounded-md">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="px-8 md:px-14 pb-20 max-w-6xl mx-auto">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-500 mb-3">/ unified leads feed</p>
        <h1 className="serif font-light text-5xl md:text-6xl tracking-tight leading-tight">
          Every <em className="italic text-sun-500">submission</em>, one feed.
        </h1>
        <p className="mt-3 text-ink-900/70 max-w-2xl">
          Giggle orders, Portfolio intro requests, and Contact notes — newest first. Click any row to expand.
        </p>

        {/* Filter chips */}
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="leads-filters">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mr-2 inline-flex items-center gap-1">
            <Filter className="h-3 w-3" /> Filter
          </div>
          {["all", "giggle", "portfolio", "quote", "contact"].map((s) => {
            const active = filter === s;
            const meta = SOURCE_META[s];
            const label = s === "all" ? `All (${leads.length})` : `${meta.label} (${counts[s] || 0})`;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition border"
                style={{
                  background: active ? (meta?.color || "#0F1E2E") : "transparent",
                  color: active ? "#FDFBF5" : "#0F1E2E",
                  borderColor: active ? (meta?.color || "#0F1E2E") : "rgba(15,30,46,0.18)",
                }}
              >{label}</button>
            );
          })}
        </div>

        {/* Feed */}
        <div className="mt-8 bg-white rounded-xl border border-ink-900/10 overflow-hidden" data-testid="leads-list">
          {busy && leads.length === 0 ? (
            <div className="p-12 text-center mono text-[11px] uppercase tracking-[0.22em] text-ink-900/50">loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-ink-900/60">No leads in this view.</div>
          ) : (
            <ul className="divide-y divide-ink-900/8">
              {filtered.map((l) => {
                const meta = SOURCE_META[l.source] || { label: l.source, color: "#888", Icon: MessageSquare };
                const Icon = meta.Icon;
                const isOpen = openId === l.id;
                return (
                  <li key={`${l.source}-${l.id}`} className="px-5 py-4 hover:bg-cream-50/60 transition cursor-pointer" data-testid={`lead-row-${l.id}`}
                      onClick={() => setOpenId(isOpen ? null : l.id)}>
                    <div className="flex items-start gap-4">
                      <div className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1A`, color: meta.color }}>
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-xs mono uppercase tracking-[0.18em] font-medium" style={{ color: meta.color }}>{meta.label}</span>
                          <span className="font-semibold text-ink-900">{l.name || "—"}</span>
                          {l.organization && (
                            <span className="text-ink-900/65 text-sm inline-flex items-center gap-1">
                              <Building2 className="h-3 w-3" /> {l.organization}
                            </span>
                          )}
                          {l.email && (
                            <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()}
                               className="text-ink-900/55 text-xs hover:text-sun-500 inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {l.email}
                            </a>
                          )}
                          {l.engagement_pct !== null && l.engagement_pct !== undefined && (
                            <span className="text-xs mono inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                                  style={{ background: "rgba(245,180,0,0.18)", color: "#C98F00" }}>
                              <BarChart3 className="h-3 w-3" /> {l.engagement_pct}%
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-ink-900/75 truncate">{l.headline || (l.notes || "").slice(0, 100)}</div>
                        <div className="mt-1 mono text-[10px] uppercase tracking-[0.18em] text-ink-900/45">{timeAgo(l.created_at)}</div>
                      </div>
                      <div className="flex-shrink-0 text-ink-900/40 mt-1">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="mt-4 ml-13 pl-13 bg-cream-50/80 rounded-md p-4 text-sm" onClick={(e) => e.stopPropagation()} data-testid={`lead-detail-${l.id}`}>
                        {l.notes && (
                          <div className="mb-3">
                            <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 mb-1">Notes</div>
                            <div className="text-ink-900/85 whitespace-pre-wrap">{l.notes}</div>
                          </div>
                        )}
                        {l.extra && Object.keys(l.extra).length > 0 && (
                          <div className="mb-3">
                            <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 mb-1">Details</div>
                            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-ink-900/80">
                              {Object.entries(l.extra).map(([k, v]) => {
                                if (v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                                return (
                                  <li key={k} className="flex gap-2">
                                    <span className="mono uppercase tracking-[0.12em] text-ink-900/45 min-w-[120px]">{k.replace(/_/g, " ")}</span>
                                    <span className="font-medium">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                        {l.sections_viewed && l.sections_viewed.length > 0 && (
                          <div>
                            <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 mb-1">Sections viewed</div>
                            <div className="flex flex-wrap gap-1">
                              {l.sections_viewed.map((s) => (
                                <span key={s} className="px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-[0.12em] bg-sun-500/15 text-sun-700">{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
